import { CATEGORY_BY_ID } from '../categories';
import type { Registration } from '../registry';
import { RTC_GPIO_OPTIONS } from '../pins';

const t = CATEGORY_BY_ID.time.colour;
const p = CATEGORY_BY_ID.power.colour;

export const timeRegistration: Registration = {
  definitions: [
    {
      type: 'esp32_delay_ms',
      message0: 'wait %1 ms',
      args0: [{ type: 'input_value', name: 'DURATION', check: 'Number' }],
      previousStatement: null,
      nextStatement: null,
      colour: t,
      tooltip: 'Blocking delay in milliseconds.',
    },
    {
      type: 'esp32_delay_us',
      message0: 'wait %1 µs',
      args0: [{ type: 'input_value', name: 'DURATION', check: 'Number' }],
      previousStatement: null,
      nextStatement: null,
      colour: t,
      tooltip: 'Blocking delay in microseconds.',
    },
    {
      type: 'esp32_millis',
      message0: 'milliseconds since start',
      output: 'Number',
      colour: t,
      tooltip: 'Milliseconds since boot (wraps after ~49 days).',
    },
    {
      type: 'esp32_micros',
      message0: 'microseconds since start',
      output: 'Number',
      colour: t,
      tooltip: 'Microseconds since boot.',
    },
    {
      type: 'esp32_every_ms',
      message0: 'every %1 ms do %2',
      args0: [
        { type: 'field_number', name: 'INTERVAL', value: 1000, min: 1, precision: 1 },
        { type: 'input_statement', name: 'DO' },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: t,
      tooltip: 'Non-blocking scheduler: runs its body once per interval without stopping the rest of the loop.',
    },
    {
      type: 'esp32_deep_sleep_timer',
      message0: 'deep sleep for %1 seconds',
      args0: [{ type: 'input_value', name: 'SECONDS', check: 'Number' }],
      previousStatement: null,
      nextStatement: null,
      colour: p,
      tooltip: 'Enter deep sleep; the chip wakes after the given time and re-runs setup().',
    },
    {
      type: 'esp32_deep_sleep_ext',
      message0: 'deep sleep until pin %1 goes %2',
      args0: [
        { type: 'field_dropdown', name: 'PIN', options: RTC_GPIO_OPTIONS },
        {
          type: 'field_dropdown',
          name: 'LEVEL',
          options: [
            ['HIGH', '1'],
            ['LOW', '0'],
          ],
        },
      ],
      previousStatement: null,
      nextStatement: null,
      colour: p,
      tooltip: 'Enter deep sleep; wake when an RTC-capable pin reaches the given level (EXT0 wakeup).',
    },
    {
      type: 'esp32_wakeup_cause',
      message0: 'wakeup cause',
      output: 'Number',
      colour: p,
      tooltip: 'Numeric esp_sleep_get_wakeup_cause(): 0 = undefined, 2 = EXT0, 4 = timer, ...',
    },
  ],
  metas: [
    {
      type: 'esp32_delay_ms',
      category: 'time',
      label: 'Wait (ms)',
      description: 'Blocking delay in milliseconds.',
      fields: { DURATION: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_delay_us',
      category: 'time',
      label: 'Wait (µs)',
      description: 'Blocking delay in microseconds.',
      fields: { DURATION: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_millis',
      category: 'time',
      label: 'Millis',
      description: 'Value: milliseconds since boot.',
      fields: {},
    },
    {
      type: 'esp32_micros',
      category: 'time',
      label: 'Micros',
      description: 'Value: microseconds since boot.',
      fields: {},
    },
    {
      type: 'esp32_every_ms',
      category: 'time',
      label: 'Every N ms',
      description: 'Non-blocking scheduler block: body runs once per interval while the loop keeps running.',
      fields: { INTERVAL: { kind: 'number' } },
      statementInput: 'DO',
    },
    {
      type: 'esp32_deep_sleep_timer',
      category: 'power',
      label: 'Deep sleep (timer)',
      description: 'Enter deep sleep and wake after N seconds (timer wakeup).',
      fields: { SECONDS: { kind: 'value', types: 'Number' } },
    },
    {
      type: 'esp32_deep_sleep_ext',
      category: 'power',
      label: 'Deep sleep (pin)',
      description: 'Enter deep sleep; wake when an RTC-capable pin reaches HIGH or LOW (EXT0).',
      fields: { PIN: { kind: 'dropdown', options: RTC_GPIO_OPTIONS.map((o) => o[1]) }, LEVEL: { kind: 'dropdown', options: ['1', '0'] } },
    },
    {
      type: 'esp32_wakeup_cause',
      category: 'power',
      label: 'Wakeup cause',
      description: 'Value: numeric wakeup cause from esp_sleep_get_wakeup_cause().',
      fields: {},
    },
  ],
};
